import { pickCopy } from '@/lib/mycio/translations'
import { getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'

/**
 * The app shell's own words: the offline indicator and offline screen (NetworkStatusIndicator), the error
 * boundaries (app/(mycio)/error.tsx, app/global-error.tsx), the root loading screen, skip links, the chat
 * bottom sheet's handle and the one-time-code input's labels.
 *
 * Several of these render OUTSIDE the language provider (the root layout), so they read the language with
 * useMyCIOLanguageOrCookie. The offline screen used to name the old product ("MyCIO"); it now says MyCIO.
 *
 * `en` is byte-identical to the strings the components shipped with, except that rename.
 * zh-Hant is derived from `zh` by pickCopy's opencc chokepoint; only author `zh` (Simplified) here.
 */
export type MyCIOShellChromeCopy = {
  network: {
    offline: string
    noInternet: string
    checking: string
    noConnectionTitle: string
    noConnectionBody: string
    /** Follows the brand name "MyCIO", which is rendered in bold before it. */
    resumesAfterBrand: string
    waiting: string
  }
  error: { title: string; body: string; tryAgain: string }
  globalError: { title: string; tryAgain: string }
  loading: string
  skipLinks: { main: string; navigation: string; search: string }
  bottomSheet: { collapse: string; expand: string }
  otp: { group: string; input: string }
}

/** Native-speaker review status of the non-English copy in this module. */
export const NATIVE_REVIEW = {
  reviewed: false,
  authored: '2026-09-21 in-session',
  note: 'every key, 13 languages',
} as const

const en: MyCIOShellChromeCopy = {
  network: {
    offline: 'Offline',
    noInternet: 'No Internet',
    checking: 'Checking internet connection...',
    noConnectionTitle: 'No Internet Connection',
    noConnectionBody: 'Please wait until your internet connection is restored. Do not refresh or navigate away.',
    resumesAfterBrand: ' will automatically resume once your connection is restored.',
    waiting: 'Waiting for connection...',
  },
  error: {
    title: 'This page hit a problem',
    body: 'Nothing was changed or lost — this is a display problem on our side, and it has been reported. Your plan and your data are untouched.',
    tryAgain: 'Try again',
  },
  globalError: { title: 'Something went wrong', tryAgain: 'Try again' },
  loading: 'Loading...',
  skipLinks: { main: 'Skip to main content', navigation: 'Skip to navigation', search: 'Skip to search' },
  bottomSheet: { collapse: 'Collapse chat', expand: 'Expand chat' },
  otp: { group: 'Verification code', input: 'Enter verification code' },
}

const es: MyCIOShellChromeCopy = {
  network: {
    offline: 'Sin conexión',
    noInternet: 'Sin internet',
    checking: 'Comprobando la conexión a internet…',
    noConnectionTitle: 'Sin conexión a internet',
    noConnectionBody: 'Espera a que se restablezca tu conexión a internet. No actualices ni salgas de la página.',
    resumesAfterBrand: ' se reanudará automáticamente en cuanto se restablezca la conexión.',
    waiting: 'Esperando la conexión…',
  },
  error: {
    title: 'Esta página ha tenido un problema',
    body: 'No se ha cambiado ni perdido nada: es un problema de visualización por nuestra parte y ya se ha notificado. Tu plan y tus datos están intactos.',
    tryAgain: 'Reintentar',
  },
  globalError: { title: 'Algo ha salido mal', tryAgain: 'Reintentar' },
  loading: 'Cargando…',
  skipLinks: { main: 'Saltar al contenido principal', navigation: 'Saltar a la navegación', search: 'Saltar a la búsqueda' },
  bottomSheet: { collapse: 'Contraer el chat', expand: 'Ampliar el chat' },
  otp: { group: 'Código de verificación', input: 'Introduce el código de verificación' },
}

const pt: MyCIOShellChromeCopy = {
  network: {
    offline: 'Sem conexão',
    noInternet: 'Sem internet',
    checking: 'Verificando a conexão com a internet…',
    noConnectionTitle: 'Sem conexão com a internet',
    noConnectionBody: 'Aguarde até que sua conexão com a internet seja restabelecida. Não atualize nem saia da página.',
    resumesAfterBrand: ' será retomado automaticamente assim que a conexão for restabelecida.',
    waiting: 'Aguardando a conexão…',
  },
  error: {
    title: 'Esta página encontrou um problema',
    body: 'Nada foi alterado nem perdido — é um problema de exibição do nosso lado, e ele já foi relatado. Seu plano e seus dados estão intactos.',
    tryAgain: 'Tentar novamente',
  },
  globalError: { title: 'Algo deu errado', tryAgain: 'Tentar novamente' },
  loading: 'Carregando…',
  skipLinks: { main: 'Pular para o conteúdo principal', navigation: 'Pular para a navegação', search: 'Pular para a busca' },
  bottomSheet: { collapse: 'Recolher o chat', expand: 'Expandir o chat' },
  otp: { group: 'Código de verificação', input: 'Digite o código de verificação' },
}

const fr: MyCIOShellChromeCopy = {
  network: {
    offline: 'Hors ligne',
    noInternet: 'Pas d’internet',
    checking: 'Vérification de la connexion internet…',
    noConnectionTitle: 'Pas de connexion internet',
    noConnectionBody: 'Veuillez patienter jusqu’au rétablissement de votre connexion internet. N’actualisez pas la page et ne la quittez pas.',
    resumesAfterBrand: ' reprendra automatiquement dès que votre connexion sera rétablie.',
    waiting: 'En attente de connexion…',
  },
  error: {
    title: 'Cette page a rencontré un problème',
    body: 'Rien n’a été modifié ni perdu — il s’agit d’un problème d’affichage de notre côté, et il a été signalé. Votre plan et vos données sont intacts.',
    tryAgain: 'Réessayer',
  },
  globalError: { title: 'Un problème est survenu', tryAgain: 'Réessayer' },
  loading: 'Chargement…',
  skipLinks: { main: 'Aller au contenu principal', navigation: 'Aller à la navigation', search: 'Aller à la recherche' },
  bottomSheet: { collapse: 'Réduire le chat', expand: 'Agrandir le chat' },
  otp: { group: 'Code de vérification', input: 'Saisissez le code de vérification' },
}

const de: MyCIOShellChromeCopy = {
  network: {
    offline: 'Keine Verbindung',
    noInternet: 'Kein Internet',
    checking: 'Internetverbindung wird geprüft…',
    noConnectionTitle: 'Keine Internetverbindung',
    noConnectionBody: 'Bitte warten Sie, bis Ihre Internetverbindung wiederhergestellt ist. Laden Sie die Seite nicht neu und verlassen Sie sie nicht.',
    resumesAfterBrand: ' wird automatisch fortgesetzt, sobald Ihre Verbindung wiederhergestellt ist.',
    waiting: 'Warten auf Verbindung…',
  },
  error: {
    title: 'Auf dieser Seite ist ein Problem aufgetreten',
    body: 'Es wurde nichts geändert und nichts ist verloren – es handelt sich um ein Anzeigeproblem auf unserer Seite, das bereits gemeldet wurde. Ihr Plan und Ihre Daten sind unberührt.',
    tryAgain: 'Erneut versuchen',
  },
  globalError: { title: 'Etwas ist schiefgelaufen', tryAgain: 'Erneut versuchen' },
  loading: 'Wird geladen…',
  skipLinks: { main: 'Zum Hauptinhalt springen', navigation: 'Zur Navigation springen', search: 'Zur Suche springen' },
  bottomSheet: { collapse: 'Chat einklappen', expand: 'Chat ausklappen' },
  otp: { group: 'Bestätigungscode', input: 'Bestätigungscode eingeben' },
}

const it: MyCIOShellChromeCopy = {
  network: {
    offline: 'Non in linea',
    noInternet: 'Nessuna connessione a internet',
    checking: 'Verifica della connessione a internet…',
    noConnectionTitle: 'Nessuna connessione a internet',
    noConnectionBody: 'Attendi che la connessione a internet venga ripristinata. Non aggiornare la pagina e non uscire.',
    resumesAfterBrand: ' riprenderà automaticamente non appena la connessione sarà ripristinata.',
    waiting: 'In attesa di connessione…',
  },
  error: {
    title: 'Questa pagina ha riscontrato un problema',
    body: 'Non è stato modificato né perso nulla: si tratta di un problema di visualizzazione da parte nostra, già segnalato. Il tuo piano e i tuoi dati sono intatti.',
    tryAgain: 'Riprova',
  },
  globalError: { title: 'Si è verificato un errore', tryAgain: 'Riprova' },
  loading: 'Caricamento…',
  skipLinks: { main: 'Vai al contenuto principale', navigation: 'Vai alla navigazione', search: 'Vai alla ricerca' },
  bottomSheet: { collapse: 'Comprimi la chat', expand: 'Espandi la chat' },
  otp: { group: 'Codice di verifica', input: 'Inserisci il codice di verifica' },
}

const ru: MyCIOShellChromeCopy = {
  network: {
    offline: 'Нет сети',
    noInternet: 'Нет интернета',
    checking: 'Проверяем подключение к интернету…',
    noConnectionTitle: 'Нет подключения к интернету',
    noConnectionBody: 'Дождитесь восстановления подключения к интернету. Не обновляйте страницу и не уходите с неё.',
    resumesAfterBrand: ' автоматически продолжит работу, как только подключение восстановится.',
    waiting: 'Ожидаем подключения…',
  },
  error: {
    title: 'На этой странице возникла проблема',
    body: 'Ничего не изменилось и не потерялось — это проблема отображения на нашей стороне, и мы уже получили о ней сообщение. Ваш план и данные в порядке.',
    tryAgain: 'Повторить',
  },
  globalError: { title: 'Что-то пошло не так', tryAgain: 'Повторить' },
  loading: 'Загрузка…',
  skipLinks: { main: 'Перейти к основному содержанию', navigation: 'Перейти к навигации', search: 'Перейти к поиску' },
  bottomSheet: { collapse: 'Свернуть чат', expand: 'Развернуть чат' },
  otp: { group: 'Код подтверждения', input: 'Введите код подтверждения' },
}

const zh: MyCIOShellChromeCopy = {
  network: {
    offline: '离线',
    noInternet: '无法连接互联网',
    checking: '正在检查网络连接…',
    noConnectionTitle: '没有网络连接',
    noConnectionBody: '请等待网络连接恢复。请勿刷新或离开此页面。',
    resumesAfterBrand: ' 会在连接恢复后自动继续。',
    waiting: '正在等待连接…',
  },
  error: {
    title: '此页面出现问题',
    body: '没有任何内容被更改或丢失——这是我们这边的显示问题，已经上报。你的计划和数据均未受影响。',
    tryAgain: '重试',
  },
  globalError: { title: '出了点问题', tryAgain: '重试' },
  loading: '加载中…',
  skipLinks: { main: '跳到主要内容', navigation: '跳到导航', search: '跳到搜索' },
  bottomSheet: { collapse: '收起聊天', expand: '展开聊天' },
  otp: { group: '验证码', input: '输入验证码' },
}

const ja: MyCIOShellChromeCopy = {
  network: {
    offline: 'オフライン',
    noInternet: 'インターネット未接続',
    checking: 'インターネット接続を確認しています…',
    noConnectionTitle: 'インターネットに接続されていません',
    noConnectionBody: 'インターネット接続が回復するまでお待ちください。ページを再読み込みしたり、移動したりしないでください。',
    resumesAfterBrand: ' は接続が回復すると自動的に再開します。',
    waiting: '接続を待っています…',
  },
  error: {
    title: 'このページで問題が発生しました',
    body: '変更や消失は一切ありません。当社側の表示の問題で、すでに報告済みです。プランとデータはそのまま残っています。',
    tryAgain: '再試行',
  },
  globalError: { title: '問題が発生しました', tryAgain: '再試行' },
  loading: '読み込み中…',
  skipLinks: { main: 'メインコンテンツへ移動', navigation: 'ナビゲーションへ移動', search: '検索へ移動' },
  bottomSheet: { collapse: 'チャットを閉じる', expand: 'チャットを開く' },
  otp: { group: '確認コード', input: '確認コードを入力' },
}

const ko: MyCIOShellChromeCopy = {
  network: {
    offline: '오프라인',
    noInternet: '인터넷 연결 없음',
    checking: '인터넷 연결을 확인하는 중…',
    noConnectionTitle: '인터넷에 연결되어 있지 않습니다',
    noConnectionBody: '인터넷 연결이 복구될 때까지 기다려 주세요. 새로 고치거나 페이지를 벗어나지 마세요.',
    resumesAfterBrand: '은(는) 연결이 복구되면 자동으로 다시 시작됩니다.',
    waiting: '연결을 기다리는 중…',
  },
  error: {
    title: '이 페이지에 문제가 발생했습니다',
    body: '변경되거나 사라진 것은 없습니다. 저희 쪽 표시 문제이며 이미 보고되었습니다. 계획과 데이터는 그대로입니다.',
    tryAgain: '다시 시도',
  },
  globalError: { title: '문제가 발생했습니다', tryAgain: '다시 시도' },
  loading: '불러오는 중…',
  skipLinks: { main: '본문으로 건너뛰기', navigation: '탐색 메뉴로 건너뛰기', search: '검색으로 건너뛰기' },
  bottomSheet: { collapse: '채팅 접기', expand: '채팅 펼치기' },
  otp: { group: '인증 코드', input: '인증 코드 입력' },
}

const ar: MyCIOShellChromeCopy = {
  network: {
    offline: 'غير متصل',
    noInternet: 'لا يوجد اتصال بالإنترنت',
    checking: 'جارٍ التحقق من الاتصال بالإنترنت…',
    noConnectionTitle: 'لا يوجد اتصال بالإنترنت',
    noConnectionBody: 'يُرجى الانتظار حتى يعود اتصالك بالإنترنت. لا تُحدِّث الصفحة ولا تغادرها.',
    resumesAfterBrand: ' سيستأنف العمل تلقائيًا بمجرد عودة الاتصال.',
    waiting: 'في انتظار الاتصال…',
  },
  error: {
    title: 'واجهت هذه الصفحة مشكلة',
    body: 'لم يتغيّر أو يُفقد أي شيء — إنها مشكلة عرض من جهتنا، وقد أُبلغ عنها. خطتك وبياناتك سليمة.',
    tryAgain: 'حاول مجددًا',
  },
  globalError: { title: 'حدث خطأ ما', tryAgain: 'حاول مجددًا' },
  loading: 'جارٍ التحميل…',
  skipLinks: { main: 'انتقل إلى المحتوى الرئيسي', navigation: 'انتقل إلى التنقل', search: 'انتقل إلى البحث' },
  bottomSheet: { collapse: 'طيّ المحادثة', expand: 'توسيع المحادثة' },
  otp: { group: 'رمز التحقق', input: 'أدخل رمز التحقق' },
}

const hi: MyCIOShellChromeCopy = {
  network: {
    offline: 'ऑफ़लाइन',
    noInternet: 'इंटरनेट नहीं है',
    checking: 'इंटरनेट कनेक्शन जाँचा जा रहा है…',
    noConnectionTitle: 'इंटरनेट कनेक्शन नहीं है',
    noConnectionBody: 'कृपया इंटरनेट कनेक्शन वापस आने तक प्रतीक्षा करें। पेज रीफ़्रेश न करें और न ही कहीं और जाएँ।',
    resumesAfterBrand: ' कनेक्शन लौटते ही अपने-आप फिर शुरू हो जाएगा।',
    waiting: 'कनेक्शन की प्रतीक्षा है…',
  },
  error: {
    title: 'इस पेज में कोई समस्या आई',
    body: 'कुछ भी बदला या खोया नहीं है — यह हमारी ओर से दिखाने की समस्या है, और इसकी सूचना दे दी गई है। आपकी योजना और डेटा सुरक्षित हैं।',
    tryAgain: 'फिर कोशिश करें',
  },
  globalError: { title: 'कुछ गड़बड़ हो गई', tryAgain: 'फिर कोशिश करें' },
  loading: 'लोड हो रहा है…',
  skipLinks: { main: 'मुख्य सामग्री पर जाएँ', navigation: 'नेविगेशन पर जाएँ', search: 'खोज पर जाएँ' },
  bottomSheet: { collapse: 'चैट छोटा करें', expand: 'चैट बड़ा करें' },
  otp: { group: 'सत्यापन कोड', input: 'सत्यापन कोड दर्ज करें' },
}

const id: MyCIOShellChromeCopy = {
  network: {
    offline: 'Luring',
    noInternet: 'Tidak ada internet',
    checking: 'Memeriksa koneksi internet…',
    noConnectionTitle: 'Tidak ada koneksi internet',
    noConnectionBody: 'Harap tunggu hingga koneksi internet Anda pulih. Jangan memuat ulang atau meninggalkan halaman ini.',
    resumesAfterBrand: ' akan berlanjut otomatis begitu koneksi Anda pulih.',
    waiting: 'Menunggu koneksi…',
  },
  error: {
    title: 'Halaman ini mengalami masalah',
    body: 'Tidak ada yang berubah atau hilang — ini masalah tampilan di pihak kami, dan sudah dilaporkan. Rencana dan data Anda tidak tersentuh.',
    tryAgain: 'Coba lagi',
  },
  globalError: { title: 'Terjadi kesalahan', tryAgain: 'Coba lagi' },
  loading: 'Memuat…',
  skipLinks: { main: 'Langsung ke konten utama', navigation: 'Langsung ke navigasi', search: 'Langsung ke pencarian' },
  bottomSheet: { collapse: 'Ciutkan obrolan', expand: 'Luaskan obrolan' },
  otp: { group: 'Kode verifikasi', input: 'Masukkan kode verifikasi' },
}

const tr: MyCIOShellChromeCopy = {
  network: {
    offline: 'Çevrimdışı',
    noInternet: 'İnternet yok',
    checking: 'İnternet bağlantısı kontrol ediliyor…',
    noConnectionTitle: 'İnternet bağlantısı yok',
    noConnectionBody: 'Lütfen internet bağlantınız geri gelene kadar bekleyin. Sayfayı yenilemeyin veya sayfadan ayrılmayın.',
    resumesAfterBrand: ', bağlantınız geri gelir gelmez otomatik olarak devam edecek.',
    waiting: 'Bağlantı bekleniyor…',
  },
  error: {
    title: 'Bu sayfada bir sorun oluştu',
    body: 'Hiçbir şey değişmedi veya kaybolmadı — bu bizim tarafımızdaki bir görüntüleme sorunu ve bildirildi. Planınız ve verileriniz olduğu gibi duruyor.',
    tryAgain: 'Tekrar dene',
  },
  globalError: { title: 'Bir şeyler ters gitti', tryAgain: 'Tekrar dene' },
  loading: 'Yükleniyor…',
  skipLinks: { main: 'Ana içeriğe geç', navigation: 'Gezinmeye geç', search: 'Aramaya geç' },
  bottomSheet: { collapse: 'Sohbeti daralt', expand: 'Sohbeti genişlet' },
  otp: { group: 'Doğrulama kodu', input: 'Doğrulama kodunu girin' },
}

const byLanguage: Partial<Record<MyCIOLanguageCode, MyCIOShellChromeCopy>> = {
  en, es, pt, fr, de, it, ru, zh, ja, ko, ar, hi, id, tr,
}

export function getMyCIOShellChromeCopy(language: string | null | undefined): MyCIOShellChromeCopy {
  return pickCopy(getMyCIOLanguage(language).code, byLanguage, en)
}
